import { Minus, TrendingDown, TrendingUp } from "lucide-react"

import { TONO_FUENTE } from "@/componentes/tesoreria/tipos"
import { Insignia } from "@/componentes/ui/Insignia"
import { comoFecha } from "@/lib/fechas"

import { comoTasa, comoVariacion, diaYMes, sentidoDe } from "./formato"
import { Minigrafico } from "./Minigrafico"
import type { TasaDelPanel } from "./tipos"

const FLECHA = { sube: TrendingUp, baja: TrendingDown, igual: Minus } as const

/**
 * Una divisa: lo que cuesta hoy en bolívares, cuánto se movió y su forma reciente.
 *
 * ⚠ **En bolívares por 1 unidad**, como se lee aquí y como la enseñan el 2.0 y
 * `constructora_v2`: «el euro está a 968», no «0,8599 euros por dólar». El número del
 * ledger no se esconde: va al pie, porque es el que se audita.
 *
 * ⚠ **La variación no lleva color de «bien» o «mal».** Que el dólar suba es malo para
 * quien paga en bolívares y bueno para quien cobra en dólares, y las dos referencias
 * lo pintaban al revés una de la otra. Lleva flecha y signo, que dicen lo que pasó sin
 * juzgarlo; el verde y el rojo quedan para los estados.
 *
 * ⚠ **Sin tasa se dice «sin tasa», nunca cero**: un cero se lee como que la divisa vale
 * cero, que es la lectura que lleva a conversiones a cero.
 *
 * Es un botón: elige la divisa cuyo historial se ve debajo.
 */
export function TarjetaTasa({
  tasa,
  elegida,
  onElegir,
}: {
  tasa: TasaDelPanel
  elegida: boolean
  onElegir: () => void
}) {
  const { actual, anterior } = tasa
  const sentido = sentidoDe(tasa.variacion)
  const Flecha = sentido === null ? null : FLECHA[sentido]
  // El BS se presenta como «USD»: su tasa ya son bolívares por dólar y no hay otro
  // número que guardar. El euro y el USDT sí se guardan distinto de como se leen.
  const derivada = tasa.moneda === tasa.codigo

  return (
    <button
      type="button"
      aria-pressed={elegida}
      onClick={onElegir}
      data-testid={`tasa-${tasa.codigo}`}
      className="tarjeta-interactiva flex h-full w-full flex-col gap-3 p-4 text-left"
      style={
        elegida
          ? {
              borderColor: "var(--primary-texto)",
              boxShadow: "inset 0 0 0 1px var(--primary-texto)",
            }
          : undefined
      }
    >
      <span className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{tasa.moneda}</span>
            {actual && (
              <Insignia tono={TONO_FUENTE[actual.fuente] ?? "neutro"}>{actual.fuente}</Insignia>
            )}
          </span>
          <span className="block truncate text-xs" style={{ color: "var(--fg-muted)" }}>
            {tasa.nombre}
          </span>
        </span>
        <Minigrafico puntos={tasa.serie} />
      </span>

      <span className="block">
        {actual === null ? (
          <>
            <span className="block text-2xl font-semibold" style={{ color: "var(--fg-muted)" }}>
              sin tasa
            </span>
            <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Todavía no se ha registrado ninguna.
            </span>
          </>
        ) : actual.bs_por_unidad === null ? (
          <>
            <span className="block text-2xl font-semibold" style={{ color: "var(--fg-muted)" }}>
              sin dólar
            </span>
            <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
              No hay tasa del dólar con qué pasarla a bolívares.
            </span>
          </>
        ) : (
          <>
            {/* Cifras proporcionales y no tabulares: es un número suelto y grande, no
                una columna que alinear. */}
            <span className="text-2xl font-semibold">{comoTasa(actual.bs_por_unidad)}</span>
            <span className="ml-1 text-xs" style={{ color: "var(--fg-muted)" }}>
              Bs por 1 {tasa.moneda}
            </span>
          </>
        )}
      </span>

      {actual !== null && (
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
          {Flecha !== null && tasa.variacion !== null && anterior !== null ? (
            <>
              <Flecha size={14} aria-hidden={true} />
              <span className="font-medium">{comoVariacion(tasa.variacion)}</span>
              <span style={{ color: "var(--fg-muted)" }}>
                vs {diaYMes(anterior.fecha_valor)} ({comoTasa(anterior.bs_por_unidad)})
              </span>
            </>
          ) : (
            <span style={{ color: "var(--fg-muted)" }}>Sin un día anterior con qué comparar</span>
          )}
        </span>
      )}

      {actual !== null && (
        <span className="mt-auto text-xs" style={{ color: "var(--fg-muted)" }}>
          Vigente desde {comoFecha(actual.fecha_valor)}
          {derivada && (
            <span className="block tabular-nums">
              Guardada como {comoTasa(actual.tasa, 6)} {tasa.moneda} por 1 USD
            </span>
          )}
        </span>
      )}
    </button>
  )
}
